/**
 * Service de produtos
 */

import { api } from './api';
import { API_ENDPOINTS, Produto, ObterProdutoRequest, ObterProdutoPedidoRequest, ProdutoPrevisaoVendas, ObterProdutoPrevisaoVendasRequest } from '@types';
import { SessionUtil } from '@utils';

export class ProdutoService {
  /**
   * Obtém produtos de uma classificação
   */
  async obterProdutos(
    classificacao: string,
    procurar: string = ''
  ): Promise<Produto[]> {
    try {
      const vendedor = SessionUtil.getVendedor();

      if (!vendedor) {
        throw new Error('Vendedor não encontrado na sessão');
      }

      const params: ObterProdutoRequest = {
        classificacao,
        procurar,
        vendedor: vendedor.vendCodigo,
      };

      const response = await api.post<Produto[]>(API_ENDPOINTS.OBTER_PRODUTO, params);
      return response;
    } catch (error) {
      console.error('Erro ao obter produtos:', error);
      throw error;
    }
  }


  /**
   * Obtém produtos da previsao ativa do vendedor
   */
  async obterProdutoPrevisaoPedido(
    classificacao: string,
    procurar: string = ''
  ): Promise<Produto[]> {
    try {
      const vendedor = SessionUtil.getVendedor();

      if (!vendedor) {
        throw new Error('Vendedor não encontrado na sessão');
      }

      const params: ObterProdutoPedidoRequest = {
        classificacao,
        procurar,
        vendedor: vendedor.vendCodigo,
        previsao: vendedor.indPrevisao,
      };

      const response = await api.post<Produto[]>(API_ENDPOINTS.OBTER_PRODUTO_PEDIDO, params);
      return response;
    } catch (error) {
      console.error('Erro ao obter produtos:', error);
      throw error;
    }
  }


  /**
   * Obtém produto por código
   */
  async obterPorCodigo(
    codigo: string,
    classificacao: string
  ): Promise<Produto | null> {
    try {
      const produtos = await this.obterProdutos(classificacao);
      return produtos.find((p) => p.codigo === codigo) || null;
    } catch (error) {
      console.error('Erro ao obter produto:', error);
      throw error;
    }
  }

  /**
   * Busca produtos
   */
  async buscar(classificacao: string, termo: string): Promise<Produto[]> {
    try {
      return await this.obterProdutos(classificacao, termo);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      throw error;
    }
  }

  /**
   * Calcula disponibilidade do produto
   */
  calcularDisponibilidade(produto: Produto): number {
    const vendedor = SessionUtil.getVendedor();

    if (!vendedor) return 0;

    const { indPrevisao, indPedido } = vendedor;

    // Regra 1: previsao = 0 + pedido = 0, disponivel = estoque
    if (indPrevisao === 0 && indPedido === 0) {
      return produto.estoque;
    }

    // Regra 2: previsao = 1 + pedido = 0, disponivel = estoque
    if (indPrevisao === 1 && indPedido === 0) {
      return produto.estoque;
    }

    // Regra 3: previsao = 0 + pedido = 1, disponivel = vendedor_estoque
    if (indPrevisao === 0 && indPedido === 1) {
      return produto.vendedor_estoque;
    }

    return 0;
  }

  /**
   * Verifica se produto tem estoque disponível
   */
  hasEstoqueDisponivel(produto: Produto, quantidade: number = 1): boolean {
    const disponivel = this.calcularDisponibilidade(produto);
    return disponivel >= quantidade;
  }

  /**
   * Valida preço do produto
   */
  validarPreco(produto: Produto, preco: number): {
    isValid: boolean;
    message?: string;
  } {
    const precoMin = parseFloat(produto.precoMin);
    
    if (preco < precoMin) {
      return {
        isValid: false,
        message: `Preço não pode ser menor que ${precoMin.toFixed(2)}`,
      };
    }

    return { isValid: true };
  }

  /**
   * Filtra produtos por termo de busca
   */
  filtrarProdutos(produtos: Produto[], termo: string): Produto[] {
    if (!termo) return produtos;

    const termoLower = termo.toLowerCase();

    return produtos.filter(
      (p) =>
        p.nome.toLowerCase().includes(termoLower) ||
        p.codigo.toLowerCase().includes(termoLower)
    );
  }

  /**
   * Ordena produtos
   */
  ordenarProdutos(
    produtos: Produto[],
    campo: keyof Produto = 'nome',
    ordem: 'asc' | 'desc' = 'asc'
  ): Produto[] {
    return [...produtos].sort((a, b) => {
      const valorA = a[campo];
      const valorB = b[campo];

      if (typeof valorA === 'string' && typeof valorB === 'string') {
        return ordem === 'asc'
          ? valorA.localeCompare(valorB)
          : valorB.localeCompare(valorA);
      }

      if (typeof valorA === 'number' && typeof valorB === 'number') {
        return ordem === 'asc' ? valorA - valorB : valorB - valorA;
      }

      return 0;
    });
  }

  /**
   * Obtém previsões de vendas de um produto
   */
  async obterPrevisaoVendas(
    previsao: number,
    produto: string
  ): Promise<ProdutoPrevisaoVendas[]> {
    try {
      const params: ObterProdutoPrevisaoVendasRequest = {
        previsao: previsao.toString(),
        produto,
      };

      console.log('[PRODUTO] Chamando API obterProdutoPrevisaoVendas:', {
        endpoint: API_ENDPOINTS.OBTER_PRODUTO_PREVISAO_VENDAS,
        params,
      });

      const response = await api.post<ProdutoPrevisaoVendas[]>(
        API_ENDPOINTS.OBTER_PRODUTO_PREVISAO_VENDAS,
        params
      );

      console.log('[PRODUTO] Resposta da API:', response);

      return response || [];
    } catch (error) {
      console.error('[PRODUTO] Erro ao obter previsões de vendas:', error);
      return [];
    }
  }
}

export const produtoService = new ProdutoService();